import { PlansBundle } from '@common/models';
import { mixExtraUsersMonths } from '@common/utils/extra-users.util';

/**
 * Eventos PostHog del funnel de compra mix de «Mejora tu Fina» (ADM-766).
 * Fuente única de los nombres: el spec fija que son exactamente estos.
 */
export const MIX_PURCHASE_EVENTS = {
    viewed: 'improve_fina_viewed',
    smsSelected: 'mix_cart_sms_selected',
    smsRemoved: 'mix_cart_sms_removed',
    extraUserAdded: 'mix_cart_extra_user_added',
    extraUserRemoved: 'mix_cart_extra_user_removed',
    setupToggled: 'mix_cart_setup_toggled',
    renewalCtaClicked: 'improve_fina_renewal_cta_clicked',
    checkoutOpened: 'mix_checkout_opened',
    blocked: 'mix_purchase_blocked',
    reported: 'mix_purchase_reported',
    reportFailed: 'mix_purchase_report_failed',
    cardPaid: 'mix_purchase_card_paid',
    cardPending: 'mix_card_charge_pending',
    cardFailed: 'mix_card_charge_failed',
    abandoned: 'mix_checkout_abandoned',
} as const;

/**
 * Por qué no se pudo comprar:
 * - `renewal_due`: el carrito lleva cupos y la próxima fecha de pago ya venció (409 `EXTRA_USERS_MIX_RENEWAL_DUE`).
 * - `empty_cart`: la intención de otra pantalla no dejó nada en el carrito.
 * - `no_permission`: sin `create-and-edit-users` la intención no precarga cupos.
 */
export type MixPurchaseBlockedReason = 'renewal_due' | 'empty_cart' | 'no_permission';

/** Dónde se cortó la compra (`intent` no abre el modal; el resto, con el modal abierto). */
export type MixPurchaseBlockedStage = 'intent' | 'report_precheck' | 'report' | 'card_charge';

export type MixCartItem = 'sms' | 'users' | 'setup' | 'controlNumbers';

/** Entrada mínima del carrito; subtotal, IVA y retención solo los tiene el carrito cotizado. */
export interface MixCartAnalyticsInput {
    smsQuantity: number;
    /** Cantidad del paquete de números de control (ADM-732); ausente ≡ 0. */
    controlNumbersQuantity?: number;
    extraUsersToAdd: number;
    includeSetup: boolean;
    amountUsd: number;
    subtotalUsd?: number;
    ivaUsd?: number;
    retentionUsd?: number;
}

export interface MixCartAnalyticsProps {
    sms_quantity: number;
    control_numbers_quantity: number;
    extra_users: number;
    include_setup: boolean;
    amount_usd: number | null;
    subtotal_usd: number | null;
    iva_usd: number | null;
    retention_usd: number | null;
    items: MixCartItem[];
    items_count: number;
    extra_users_months: number | null;
    payment_frequency: string | null;
}

/**
 * Tipos de línea del carrito, en el orden de `includesTypes` de `POST /plans/pay`
 * (`controlNumbers` va al final, como lo mandaba ADM-732).
 * Lo usa el body del reporte y las props de analytics: no vuelvas a derivarlo a mano.
 */
export function mixCartItems(cart: Pick<MixCartAnalyticsInput, 'smsQuantity' | 'controlNumbersQuantity' | 'extraUsersToAdd' | 'includeSetup'> | null | undefined): MixCartItem[] {
    if (!cart) {
        return [];
    }
    const items: MixCartItem[] = [];
    if (cart.smsQuantity > 0) {
        items.push('sms');
    }
    if (cart.extraUsersToAdd > 0) {
        items.push('users');
    }
    if (cart.includeSetup) {
        items.push('setup');
    }
    if (Number(cart.controlNumbersQuantity || 0) > 0) {
        items.push('controlNumbers');
    }
    return items;
}

const finiteOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/**
 * Props comunes de los eventos mix. Siempre devuelve las mismas claves: lo que falta
 * viaja como `null`, así los breakdowns de PostHog no dependen del punto de captura.
 * `now` es el instante con el que se cotizaron los cupos (el modal pasa el del día del
 * pago reportado); sin él, el reloj.
 */
export function buildMixCartProps(cart: MixCartAnalyticsInput | null | undefined, bundle?: PlansBundle | null, now?: Date): MixCartAnalyticsProps {
    const items = mixCartItems(cart);
    return {
        sms_quantity: Math.max(0, Number(cart?.smsQuantity) || 0),
        control_numbers_quantity: Math.max(0, Number(cart?.controlNumbersQuantity) || 0),
        extra_users: Math.max(0, Number(cart?.extraUsersToAdd) || 0),
        include_setup: cart?.includeSetup === true,
        amount_usd: finiteOrNull(cart?.amountUsd),
        subtotal_usd: finiteOrNull(cart?.subtotalUsd),
        iva_usd: finiteOrNull(cart?.ivaUsd),
        retention_usd: finiteOrNull(cart?.retentionUsd),
        items,
        items_count: items.length,
        extra_users_months: bundle ? mixExtraUsersMonths(bundle.nextPayment?.dateToPay ?? null, bundle.paymentFrequency ?? null, now) : null,
        payment_frequency: bundle?.paymentFrequency ? String(bundle.paymentFrequency).trim().toLowerCase() : null,
    };
}
